import Bird from 'assets/images/cart-bird.svg?react'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { formatPercentage } from 'common/services/format'
import { LeaseSlotSkeletonList } from 'features/Booking/components/LeaseSlotSkeletonList/LeaseSlotSkeletonList'
import type { IDiscount, IRoomTimeSlot } from 'features/Booking/interfaces/ILeaseInfoRoom'
import { useFetchRoomSlotsQuery } from 'features/Booking/state/api/bookingApi'
import {
  SET_NEW_LEASE_SELECTED_SLOTS,
  UPDATE_NEW_LEASE_ROOM_DAYS,
} from 'features/Booking/state/slices/leaseSlice'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router'
import { type CalculatedDiscount, calculateTotalDiscount, groupSlots } from './booking.utils'
import { BookingSlotGroup } from './BookingSlotGroup'
import styles from './bookingSlots.module.scss'

type Props = {
  isLoading?: boolean
  date: string
  defaultSelectedSlotsIds: number[]
  setCalculatedDiscount?: (discount: CalculatedDiscount) => void
}

const EMPTY_SLOTS: IRoomTimeSlot[] = []
const EMPTY_DISCOUNT: IDiscount[] = []
const EMPTY_SELECTED_SLOTS_IDS: number[] = []

const BookingSlots = ({
  isLoading,
  date,
  defaultSelectedSlotsIds = EMPTY_SELECTED_SLOTS_IDS,
  setCalculatedDiscount = () => {},
}: Props) => {
  const dispatch = useAppDispatch()
  const { siteId } = useParams()
  const { newLeaseInfo } = useAppSelector((state) => state.leaseReducer)
  const isInitialRender = useRef(true)

  const { data, isFetching: isFetchingSlots } = useFetchRoomSlotsQuery(
    {
      roomId: newLeaseInfo.room.id,
      date,
      siteId,
    },
    {
      skip: !newLeaseInfo?.room?.id || !date,
    },
  )

  const { slots = EMPTY_SLOTS, discount = EMPTY_DISCOUNT } = data ?? {}

  const selectedSlotIds = useMemo(() => defaultSelectedSlotsIds, [defaultSelectedSlotsIds])

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
  }, [])

  const handleSlotToggle = useCallback(
    (slotId: number) => {
      const updatedIds = new Set(selectedSlotIds)

      if (updatedIds.has(slotId)) {
        updatedIds.delete(slotId)
      } else {
        updatedIds.add(slotId)
      }

      const newSelectedSlotIds = Array.from(updatedIds).sort((a, b) => a - b)
      const selectedSlotsWithData = newSelectedSlotIds
        .map((slotId) => slots.find((slot) => slot.id === slotId))
        .filter(Boolean)

      dispatch(SET_NEW_LEASE_SELECTED_SLOTS(selectedSlotsWithData))
      dispatch(UPDATE_NEW_LEASE_ROOM_DAYS())
    },
    [selectedSlotIds, slots, dispatch],
  )

  const handleGroupToggle = useCallback(
    (groupSlotIds: number[]) => {
      const prevSelectedSet = new Set(selectedSlotIds)
      const allSelected = groupSlotIds.every((id) => prevSelectedSet.has(id))

      if (allSelected) {
        groupSlotIds.forEach((id) => prevSelectedSet.delete(id))
      } else {
        groupSlotIds.forEach((id) => prevSelectedSet.add(id))
      }

      const newSelectedSlotIds = Array.from(prevSelectedSet).sort((a, b) => a - b)
      const selectedSlotsWithData = newSelectedSlotIds
        .map((slotId) => slots.find((slot) => slot.id === slotId))
        .filter(Boolean)

      dispatch(SET_NEW_LEASE_SELECTED_SLOTS(selectedSlotsWithData))
      dispatch(UPDATE_NEW_LEASE_ROOM_DAYS())
    },
    [selectedSlotIds, slots, dispatch],
  )

  const calculatedDiscount = useMemo(() => {
    return calculateTotalDiscount(selectedSlotIds, slots, discount)
  }, [selectedSlotIds, slots, discount])

  const groupedSlots = useMemo(() => {
    return groupSlots(slots)
  }, [slots])

  const formattedSlots = useMemo(() => {
    return new Intl.ListFormat('en-GB', { style: 'short', type: 'conjunction' }).format(
      Object.keys(groupedSlots),
    )
  }, [groupedSlots])

  const formattedPercentage = useMemo(() => {
    return discount
      .map(({ value }, index) =>
        index === discount.length - 1 ? formatPercentage(value) : Math.floor(value),
      )
      .join(', ')
  }, [discount])

  useEffect(() => {
    setCalculatedDiscount(calculatedDiscount)
  }, [calculatedDiscount.totalDiscount, setCalculatedDiscount])

  if (isLoading || isFetchingSlots) return <LeaseSlotSkeletonList total={3} />

  return (
    <div className={styles.container}>
      {Object.entries(groupedSlots).map(([groupName, slots]) => (
        <BookingSlotGroup
          key={groupName}
          defaultExpanded={false}
          hasIntervalTitle
          groupName={groupName}
          slots={slots}
          selectedSlots={selectedSlotIds}
          onSlotToggle={handleSlotToggle}
          onGroupToggle={handleGroupToggle}
        />
      ))}
      <div className={styles.discountsContainer}>
        <div className={styles.discountIcon}>
          <Bird />
        </div>
        <div className={styles.discountLabel}>
          Select {formattedSlots} slots {'\n'} and get {formattedPercentage} off, respectively.
        </div>
        <span className={styles.hvacSurcharge}>*after hour slots incur an HVAC surcharge</span>
      </div>
    </div>
  )
}

export default BookingSlots
